import React from "react";
import { Button, Modal, PlusIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { isBefore, parse } from "date-fns";
import { get, isNil } from "lodash";
import { useTranslation } from "react-i18next";
import { useRouteMatch } from "react-router-dom";

import GrRatingForm from "~/components/Forms/GrRatingForm";
import Wrapper from "~/shared/ui/Wrapper";
import {
  useGetLocationData,
  useGoToPreviousPage,
  useSetLocationData,
} from "~/hooks/locationData";
import { getIndicatorName, setDefaultSelectR } from "~/libs/getData";
import { getFormatDate } from "~/libs/getFormatDate";
import {
  useDeleteListRatingMutation,
  useGetGrRatingsQuery,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { ConfirmModal } from "~/shared/ui/ConfirmModal";
import { TableV2 } from "~/shared/ui/TableV2";

import { getEditorGRIndicatorsColumns } from "./model/tableAdapter";
import {
  EDITOR_MANAGING_GR_INDICATORS_OPTIONS,
  EDITOR_MANAGING_GR_INDICATORS_PERIOD_OPTIONS,
} from "./options";

const EditorGRIndicators = () => {
  const { t } = useTranslation();
  const language = localStorage.getItem("language");
  const { params } = useRouteMatch();
  const setData = useSetLocationData();
  const { hasPermission } = usePermissions();
  const { openModal, openChildModal, currentId, dateBegin } =
    useGetLocationData();
  const goBack = useGoToPreviousPage();
  const { data, loading, refetch } = useGetGrRatingsQuery({
    variables: {
      RatingsVersionId: params["id"],
    },
    fetchPolicy: "cache-and-network",
  });
  const [deleteListRatingMutation] = useDeleteListRatingMutation({
    variables: { id: currentId },
    onCompleted() {
      refetch();
    },
  });

  const isAdd = openModal === "Add";
  const isEdit = openModal === "Edit";
  const isView = openChildModal === "View";

  const deleteListRating = () => {
    deleteListRatingMutation();
    handleCloseModal();
  };

  const grRatings = get(data, "GlobalRatingGetter.getRatings", []).map(
    ({
      id,
      quarter1,
      quarter2,
      quarter1_2,
      quarter3,
      quarter2_3,
      quarter4,
      quarter3_4,
      yearValue,
      yearPercent,
      IndicatorQuarter1,
      IndicatorQuarter2,
      IndicatorQuarter1_2,
      IndicatorQuarter3,
      IndicatorQuarter2_3,
      IndicatorQuarter4,
      IndicatorQuarter3_4,
      IndicatorYearPercent,
      IndicatorYearValue,
      Parent,
      updatedAt,
      name,
      nameEn,
      round,
      format,
      number,
      sort,
      ...grr
    }) => ({
      ...grr,
      id,
      name,
      nameEn,
      number,
      quarter1,
      quarter2,
      quarter1_2,
      quarter3,
      quarter2_3,
      quarter4,
      quarter3_4,
      yearValue,
      yearPercent,
      universalName: language === "en" ? nameEn || name : name || nameEn,
      quarter1Period: IndicatorQuarter1 ? IndicatorQuarter1.period : "",
      quarter2Period: IndicatorQuarter2 ? IndicatorQuarter2.period : "",
      quarter1_2Period: IndicatorQuarter1_2 ? IndicatorQuarter1_2.period : "",
      quarter3Period: IndicatorQuarter3 ? IndicatorQuarter3.period : "",
      quarter2_3Period: IndicatorQuarter2_3 ? IndicatorQuarter2_3.period : "",
      quarter4Period: IndicatorQuarter4 ? IndicatorQuarter4.period : "",
      quarter3_4Period: IndicatorQuarter3_4 ? IndicatorQuarter3_4.period : "",
      yearPercentPeriod: IndicatorYearPercent
        ? IndicatorYearPercent.period
        : "",
      yearValuePeriod: IndicatorYearValue ? IndicatorYearValue.period : "",
      quarter1Option: setDefaultSelectR(
        quarter1,
        getIndicatorName(IndicatorQuarter1),
      ),
      quarter2Option: setDefaultSelectR(
        quarter2,
        getIndicatorName(IndicatorQuarter2),
      ),
      quarter1_2Option: setDefaultSelectR(
        quarter1_2,
        getIndicatorName(IndicatorQuarter1_2),
      ),
      quarter3Option: setDefaultSelectR(
        quarter3,
        getIndicatorName(IndicatorQuarter3),
      ),
      quarter2_3Option: setDefaultSelectR(
        quarter2_3,
        getIndicatorName(IndicatorQuarter2_3),
      ),
      quarter4Option: setDefaultSelectR(
        quarter4,
        getIndicatorName(IndicatorQuarter4),
      ),
      quarter3_4Option: setDefaultSelectR(
        quarter3_4,
        getIndicatorName(IndicatorQuarter3_4),
      ),
      yearValueOption: setDefaultSelectR(
        yearValue,
        getIndicatorName(IndicatorYearValue),
      ),
      yearPercentOption: setDefaultSelectR(
        yearPercent,
        getIndicatorName(IndicatorYearPercent),
      ),
      parentName: get(Parent, "name", ""),
      updatedAt: getFormatDate(updatedAt),
      round: isNil(round) ? "" : String(round),
      format: isNil(format) ? "" : String(format),
      sort: isNil(sort) ? "" : String(sort),
    }),
  );

  const selectedGR = grRatings.find((i) => i.id === currentId);
  const handleCloseModal = () => {
    setData("openModal", "", "openChildModal", "");
  };

  const isEditable = isBefore(
    new Date(),
    parse(dateBegin, "dd.MM.yyyy", new Date()),
  );

  const prevElemId = get(data, "GlobalRatingGetter.getRatings[0].id", "");
  const openAddModal = () => {
    setData("openModal", "Add");
  };

  const columns = getEditorGRIndicatorsColumns(
    t,
    EDITOR_MANAGING_GR_INDICATORS_OPTIONS,
    EDITOR_MANAGING_GR_INDICATORS_PERIOD_OPTIONS,
  );

  const handleRowView = (record: { id: string }) => {
    setData("openChildModal", "View", "currentId", record.id);
  };

  const handleRowEdit = (record: { id: string }) => {
    setData("openModal", "Edit", "currentId", record.id);
  };

  const handleRowDelete = (record: { id: string }) => {
    setData("openModal", "Sure", "currentId", record.id);
  };

  const formId = "GrRatingForm";

  return (
    <Wrapper.Content
      title={t("editorGR.managing_indicators")}
      goBackHandler={goBack}
      txtBtn={t("ui.back")}
      controlComponent={
        <Flex gap={10}>
          {isEditable && hasPermission("globalRatings.rating.set") && (
            <Button
              onClick={openAddModal}
              size={"middle"}
              icon={<PlusIcon size={16} />}
            >
              {t("ui.add")}
            </Button>
          )}
        </Flex>
      }
    >
      <TableV2
        columns={columns}
        dataSource={grRatings}
        loading={loading}
        onRowDelete={
          isEditable &&
          hasPermission("globalRatings.list.delete") &&
          handleRowDelete
        }
        onRowEdit={
          isEditable &&
          hasPermission("globalRatings.rating.set") &&
          handleRowEdit
        }
        onViewDetails={
          (!isEditable || !hasPermission("globalRatings.rating.set")) &&
          handleRowView
        }
      />

      <Modal
        title={
          isAdd
            ? t("editorGR.add_gr_indicators")
            : t("editorGR.edit_gr_indicators")
        }
        open={isAdd || isEdit || isView}
        onCancel={handleCloseModal}
        closable
        centered
        okButton={{
          htmlType: "submit",
          form: formId,
          children: t("ui.save"),
        }}
      >
        {(isAdd || isEdit || isView) && (
          <GrRatingForm
            setCloseModal={handleCloseModal}
            refetch={refetch}
            prevElemId={isAdd ? prevElemId : ""}
            GRRrating={isEdit || isView ? selectedGR : ""}
            isEditable={isEditable && hasPermission("globalRatings.rating.set")}
            formId={formId}
          />
        )}
      </Modal>

      <ConfirmModal
        title={t("indicators.indicator_del")}
        open={openModal === "Sure"}
        onConfirm={deleteListRating}
        onCancel={handleCloseModal}
        centered
        closable
      />
    </Wrapper.Content>
  );
};
export default EditorGRIndicators;
